import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosResponseHeaders } from "axios";
import { saveAs } from "file-saver";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  NCBPReportsParams,
  NCBPReportsResponseSummary,
} from "../../../../../api/services/reports/models";
import {
  useExportNCBPReport,
  useGetNCBPReports,
} from "../../../../../api/services/reports/reportsApi";
import { MuiBox } from "../../../../../components";
import { FORM_ERROR_TIMEOUT_LONG } from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import {
  convertToUTCFormat,
  getFieldUndefinedForPayloadParams,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../redux/slices/appState/appStateSlice";
import { getFileNameFromHeader } from "../../../helper";
import {
  NCBPReportFormKeys,
  NCBPReportFormKeyTypes,
} from "../../../modelsAndSchemas/models";
import { NCBPReportSearchFormSchema } from "../../../modelsAndSchemas/ValidationSchema";
import { reportsSearchReportsSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { NCBPReportForm } from "../ncbpReportForm/NCBPReportForm";
import { NCBPReportsList } from "../ncbpReportResult/NCBPReportResult";

export const NCBPReportSearch = () => {
  const {
    commonValidationMessages: { requiredValidation },
    commonLabels: { someThingWentWrongPleaseTryAgainLater },
    useFetchLabel,
  } = useSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const getLabel = useFetchLabel(reportsSearchReportsSiteCoreKeys.header);
  const methods = useForm<NCBPReportFormKeyTypes>({
    mode: "all",
    defaultValues: {
      [NCBPReportFormKeys.dossierNumber]: "",
      [NCBPReportFormKeys.actualClientId]: "",
      [NCBPReportFormKeys.nonClientBillingPartyId]: "",
      [NCBPReportFormKeys.invoiceStartDate]: "",
      [NCBPReportFormKeys.invoiceEndDate]: "",
    },
    resolver: yupResolver(
      NCBPReportSearchFormSchema(
        {
          requiredValidation,
          invoiceStartDateValidationMessage: getLabel(
            reportsSearchReportsSiteCoreKeys.invoiceStartDateValidationMessage,
          ),
        },
        userCountryDateFormat!!,
      ),
    ),
  });
  const [state, setState] = useState<{
    ncbpReportsList: NCBPReportsResponseSummary[];
  }>({
    ncbpReportsList: [],
  });

  const {
    mutateAsync: getNCBPReports,
    data: ncbpReportsList,
    isPending: isNCBPReportsListLoading,
    isSuccess: isGetNCBPReportsListSuccess,
    isError: isGetNCBPReportsListError,
    filterParams: ncbpReportsListFilteredParams,
  } = useGetNCBPReports();

  const {
    mutateAsync: exportNCBPReport,
    isPending: isExportNCBPReportLoading,
    isError: isExportNCBPReportError,
  } = useExportNCBPReport();

  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const ncbpReportFormRef = useRef<{ reset: () => void }>(null);

  const isNCBPApiLoading =
    isNCBPReportsListLoading || isExportNCBPReportLoading;
  const isNCBPApiError = isGetNCBPReportsListError || isExportNCBPReportError;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isNCBPApiLoading,
      }),
    );

    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isNCBPApiLoading]);

  useEffect(() => {
    if (isGetNCBPReportsListSuccess) {
      setState((prevState) => ({
        ...prevState,
        ncbpReportsList: ncbpReportsList?.data ?? [],
      }));
    }
  }, [isGetNCBPReportsListSuccess]);

  useEffect(() => {
    if (isNCBPApiError) {
      displayToast(someThingWentWrongPleaseTryAgainLater);
    }
  }, [isNCBPApiError]);

  const handleContinueClick = () => {
    const {
      dossierNumber,
      actualClientId,
      nonClientBillingPartyId,
      invoiceStartDate,
      invoiceEndDate,
    } = getValues();

    let params: NCBPReportsParams = {
      dossierNumber,
      actualClientId,
      nonClientBillingPartyId,
    };

    if (isEmpty(dossierNumber)) {
      params = {
        ...params,
        invoiceStartDate: isNotEmpty(invoiceStartDate)
          ? convertToUTCFormat(invoiceStartDate, userCountryDateFormat)
          : undefined,
        invoiceEndDate: isNotEmpty(invoiceEndDate)
          ? convertToUTCFormat(invoiceEndDate, userCountryDateFormat)
          : undefined,
      };
    }

    getNCBPReports(getFieldUndefinedForPayloadParams(params));
  };

  const {
    getValues,
    handleSubmit,
    formState: { errors },
  } = methods;
  const handleSearchFormSubmit = () => {
    // Added timeout as zero validation has a delay to FORM_ERROR_TIMEOUT_SHORT.
    setTimeout(() => {
      if (isNotEmpty(errors)) return;
      handleSubmit(handleContinueClick)();
    }, FORM_ERROR_TIMEOUT_LONG);
  };

  const handleExport = async () => {
    try {
      const response = await exportNCBPReport(ncbpReportsListFilteredParams);
      const filename = getFileNameFromHeader(
        response.headers as AxiosResponseHeaders,
      );
      saveAs(response.data, filename);
    } catch {
      // toast is already handled by isExportNCBPReportError effect
    }
  };

  const onResetSearch = () => {
    setState((prevState) => ({
      ...prevState,
      ncbpReportsList: [],
    }));
  };

  return (
    <MuiBox>
      <FormProvider {...methods}>
        <NCBPReportForm
          ref={ncbpReportFormRef}
          onResetSearch={onResetSearch}
          onSubmit={handleSearchFormSubmit}
        />
      </FormProvider>
      <NCBPReportsList
        onExport={handleExport}
        ncbpReports={state.ncbpReportsList}
      />
    </MuiBox>
  );
};
